import { SiteTemplate } from "@/types/templates";

export const businessTemplate: SiteTemplate = {
  id: "business-standard",
  name: "비즈니스 표준 템플릿",
  slug: "business-standard",
  description: "중소기업을 위한 전문적인 비즈니스 웹사이트 템플릿",
  category: "BUSINESS",
  pricing: "FREE",

  // 기본 섹션 구성
  sections: {
    hero: {
      type: "hero",
      name: "메인 히어로 섹션",
      required: true,
      order: 1,
      defaultContent: {
        title: "귀하의 비즈니스를 성장시켜 보세요",
        subtitle: "전문적이고 신뢰할 수 있는 서비스로 고객의 꿈을 실현합니다",
        ctaText: "지금 시작하기",
        ctaUrl: "#contact",
        backgroundImage: "/images/templates/business/hero-bg.jpg",
        overlay: true,
      },
      settings: {
        height: "screen",
        alignment: "center",
        animation: "fadeInUp",
      },
    },

    about: {
      type: "about",
      name: "회사 소개",
      required: true,
      order: 2,
      defaultContent: {
        title: "우리는 누구인가",
        subtitle: "전문성과 혁신을 바탕으로 한 서비스",
        description:
          "저희는 고객의 성공을 위해 최선을 다하는 전문 기업입니다. 풍부한 경험과 전문 지식을 바탕으로 최고의 서비스를 제공합니다.",
        features: [
          {
            icon: "shield",
            title: "신뢰성",
            description: "고객과의 약속을 지키는 것이 저희의 최우선 가치입니다",
          },
          {
            icon: "star",
            title: "전문성",
            description: "각 분야의 전문가들이 최고의 품질을 보장합니다",
          },
          {
            icon: "heart",
            title: "고객중심",
            description:
              "고객의 니즈를 정확히 파악하고 맞춤형 솔루션을 제공합니다",
          },
        ],
        image: "/images/templates/business/about-image.jpg",
      },
    },

    services: {
      type: "services",
      name: "서비스 소개",
      required: true,
      order: 3,
      defaultContent: {
        title: "우리의 서비스",
        subtitle: "고객의 성공을 위한 전문 서비스",
        services: [
          {
            icon: "briefcase",
            title: "비즈니스 컨설팅",
            description:
              "체계적인 분석과 전략 수립으로 비즈니스 성장을 지원합니다",
            features: ["시장 분석", "전략 수립", "실행 계획"],
          },
          {
            icon: "chart",
            title: "디지털 마케팅",
            description: "온라인 마케팅 전략으로 브랜드 가치를 높입니다",
            features: ["SEO 최적화", "소셜미디어", "광고 운영"],
          },
          {
            icon: "cog",
            title: "기술 솔루션",
            description: "최신 기술을 활용한 맞춤형 솔루션을 제공합니다",
            features: ["웹 개발", "모바일 앱", "시스템 구축"],
          },
        ],
      },
    },

    testimonials: {
      type: "testimonials",
      name: "고객 후기",
      required: false,
      order: 4,
      defaultContent: {
        title: "고객의 성공 스토리",
        subtitle: "저희와 함께한 고객들의 생생한 후기",
        testimonials: [
          {
            name: "김민수",
            position: "대표이사",
            company: "스마트솔루션 주식회사",
            content:
              "전문적인 컨설팅으로 우리 회사의 매출이 200% 증가했습니다. 정말 감사합니다.",
            rating: 5,
            image: "/images/templates/business/testimonial-1.jpg",
          },
          {
            name: "이영희",
            position: "마케팅 팀장",
            company: "글로벌테크",
            content:
              "디지털 마케팅 전략이 정말 효과적이었습니다. 브랜드 인지도가 크게 향상되었어요.",
            rating: 5,
            image: "/images/templates/business/testimonial-2.jpg",
          },
        ],
      },
    },

    contact: {
      type: "contact",
      name: "연락처",
      required: true,
      order: 5,
      defaultContent: {
        title: "문의하기",
        subtitle: "궁금한 점이 있으시면 언제든 연락주세요",
        address: "서울특별시 강남구 테헤란로 123",
        phone: "02-1234-5678",
        email: "info@business.com",
        hours: "평일 09:00 - 18:00",
        mapEmbed: "",
        contactForm: {
          fields: [
            { name: "name", label: "이름", type: "text", required: true },
            { name: "email", label: "이메일", type: "email", required: true },
            { name: "phone", label: "연락처", type: "tel", required: false },
            { name: "subject", label: "제목", type: "text", required: true },
            {
              name: "message",
              label: "메시지",
              type: "textarea",
              required: true,
            },
          ],
        },
      },
    },
  },

  // 기본 스타일 설정
  styles: {
    colors: {
      primary: "#2563eb",
      secondary: "#64748b",
      accent: "#f59e0b",
      background: "#ffffff",
      foreground: "#1e293b",
      muted: "#f8fafc",
      border: "#e2e8f0",
    },
    typography: {
      fontFamily: {
        sans: ["Inter", "system-ui", "sans-serif"],
        heading: ["Inter", "system-ui", "sans-serif"],
      },
      fontSize: {
        xs: "0.75rem",
        sm: "0.875rem",
        base: "1rem",
        lg: "1.125rem",
        xl: "1.25rem",
        "2xl": "1.5rem",
        "3xl": "1.875rem",
        "4xl": "2.25rem",
        "5xl": "3rem",
      },
    },
    spacing: {
      section: "5rem",
      container: "1200px",
    },
    borderRadius: {
      sm: "0.375rem",
      md: "0.5rem",
      lg: "0.75rem",
      xl: "1rem",
    },
  },

  // 기본 설정
  settings: {
    seo: {
      titleTemplate: "%s | 비즈니스 웹사이트",
      defaultTitle: "전문 비즈니스 서비스",
      defaultDescription:
        "신뢰할 수 있는 전문 비즈니스 서비스로 귀하의 성공을 지원합니다",
    },
    navigation: {
      position: "top",
      sticky: true,
      logo: {
        text: "Business",
        image: "/images/templates/business/logo.png",
      },
      menuItems: [
        { label: "홈", href: "#hero" },
        { label: "소개", href: "#about" },
        { label: "서비스", href: "#services" },
        { label: "후기", href: "#testimonials" },
        { label: "연락처", href: "#contact" },
      ],
    },
    footer: {
      showSocial: true,
      socialLinks: [
        { platform: "facebook", url: "" },
        { platform: "instagram", url: "" },
        { platform: "linkedin", url: "" },
      ],
      showNewsletter: true,
      copyright: "© 2024 Business. All rights reserved.",
    },
    analytics: {
      googleAnalytics: "",
      facebookPixel: "",
    },
  },

  // 메타데이터
  features: [
    "반응형 디자인",
    "SEO 최적화",
    "연락 폼",
    "소셜 미디어 연동",
    "구글 애널리틱스",
  ],
  tags: ["비즈니스", "기업", "서비스", "전문", "신뢰"],
  previewImage: "/images/templates/business/preview.jpg",
  previewUrl: "/templates/business/preview",

  isActive: true,
  isPublic: true,
  version: "1.0.0",
};
